import * as React from "react"
import { router } from "@inertiajs/react"
import { Filter, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { classificationLabel, inboxFilterParams, laneLabel, statusLabel } from "@/components/inbox/helpers"
import type { InboxClient, InboxCounts, InboxFilters } from "@/components/inbox/types"
import { cn } from "@/lib/utils"

// The rare/system states exposed by the Status dropdown (Archived is a rail
// folder). Mirrors InboxController::FILTER_STATUSES.
const FILTER_STATUSES = ["snoozed", "ignored", "blocked"] as const
const LANES = ["automation", "leadgen"] as const

type FilterPatch = Partial<Record<"status" | "client_id" | "classification" | "lane", string | undefined>>

function applyFilters(filters: InboxFilters, patch: FilterPatch) {
  router.get(
    "/inbox",
    { ...inboxFilterParams(filters), ...patch },
    { preserveState: true, preserveScroll: true },
  )
}

export function InboxFilterControls({
  filters,
  clients,
  classifications,
  counts,
}: {
  readonly filters: InboxFilters
  readonly clients: readonly InboxClient[]
  readonly classifications: readonly string[]
  readonly counts: InboxCounts
}) {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const activeCount = [filters.client_id, filters.classification, filters.lane, filters.status].filter(
    Boolean,
  ).length

  // The wrapper is intentionally NOT position:relative — the panel anchors to
  // the nearest positioned ancestor (the toolbar row, which is `relative`), so
  // it aligns to the list column's right edge instead of the funnel button and
  // never overflows the narrow column.
  return (
    <div ref={containerRef} className="shrink-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Filters"
        aria-expanded={open}
        className={cn(
          "relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border",
          open || activeCount > 0
            ? "border-accent bg-accent-faded text-accent-display"
            : "border-hairline bg-page text-ink-body hover:bg-surface hover:text-ink-display",
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        {activeCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[10px] font-semibold text-white">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-2 top-full z-20 mt-1 w-64 rounded-xl border border-hairline bg-page p-4 shadow-lg">
          <div className="space-y-3">
            <div>
              <label htmlFor="inbox-filter-client">Client</label>
              <Select
                id="inbox-filter-client"
                value={filters.client_id ?? ""}
                onChange={(event) => applyFilters(filters, { client_id: event.target.value || undefined })}
                className="mt-1"
              >
                <option value="">All clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.display_name}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="inbox-filter-type">Type</label>
              <Select
                id="inbox-filter-type"
                value={filters.classification ?? ""}
                onChange={(event) =>
                  applyFilters(filters, { classification: event.target.value || undefined })
                }
                className="mt-1"
              >
                <option value="">All types</option>
                {classifications.map((classification) => (
                  <option key={classification} value={classification}>
                    {classificationLabel(classification)}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="inbox-filter-lane">Lane</label>
              <Select
                id="inbox-filter-lane"
                value={filters.lane ?? ""}
                onChange={(event) => applyFilters(filters, { lane: event.target.value || undefined })}
                className="mt-1"
              >
                <option value="">All lanes</option>
                {LANES.map((lane) => (
                  <option key={lane} value={lane}>
                    {laneLabel(lane)}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="inbox-filter-status">Status</label>
              <Select
                id="inbox-filter-status"
                value={filters.status ?? ""}
                onChange={(event) => applyFilters(filters, { status: event.target.value || undefined })}
                className="mt-1"
              >
                <option value="">Active (default)</option>
                {FILTER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {statusLabel(status)} ({counts.statuses[status] ?? 0})
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                applyFilters(filters, {
                  client_id: undefined,
                  classification: undefined,
                  lane: undefined,
                  status: undefined,
                })
              }
            >
              Clear all
            </Button>
            <Button variant="soft" size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function InboxFilterPills({
  filters,
  clients,
}: {
  readonly filters: InboxFilters
  readonly clients: readonly InboxClient[]
}) {
  const pills: { readonly key: keyof FilterPatch; readonly label: string }[] = []
  if (filters.client_id) {
    const client = clients.find((c) => String(c.id) === String(filters.client_id))
    pills.push({ key: "client_id", label: client?.display_name ?? "Client" })
  }
  if (filters.classification) {
    pills.push({ key: "classification", label: classificationLabel(filters.classification) })
  }
  if (filters.lane) pills.push({ key: "lane", label: laneLabel(filters.lane) })
  if (filters.status) pills.push({ key: "status", label: statusLabel(filters.status) })

  if (pills.length === 0) return null

  return (
    <>
      {pills.map((pill) => (
        <button
          key={pill.key}
          type="button"
          onClick={() => applyFilters(filters, { [pill.key]: undefined })}
          aria-label={`Remove ${pill.label} filter`}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-accent bg-accent-faded px-2.5 py-0.5 text-xs text-accent-display hover:opacity-80"
        >
          {pill.label}
          <X className="h-3 w-3" />
        </button>
      ))}
    </>
  )
}
